import ApiService from "./api.service";

export type BackendFlightDirection = "ARRIVAL" | "DEPARTURE";

export enum FlightBadgeKind {
  CANCELLED = "CANCELLED",
  LANDED = "LANDED",
  DEPARTED = "DEPARTED",
  GATE_CLOSED = "GATE_CLOSED",
  FINAL_CALL = "FINAL_CALL",
  BOARDING = "BOARDING",
  DELAYED = "DELAYED",
  EARLY = "EARLY",
  ON_TIME = "ON_TIME",
  STATUS_UNAVAILABLE = "STATUS_UNAVAILABLE",
}

export interface RecaptchaSiteKeyResponse {
  enabled: boolean;
  siteKeys: {
    ios: string | null;
    android: string | null;
    web: string | null;
    checkbox: string | null;
  };
}

export interface FlightCardResponse {
  designator: string;
  direction: BackendFlightDirection;
  otherAirport: string;
  scheduled: { local: string; utc: string };
  estimated: { local: string; utc: string } | null;
  badge: FlightBadgeKind;
  badgeDelayMinutes: number | null;
  hall: string | null;
  terminal: string | null;
  gate: string | null;
  gateClosingTime: string | null;
  arrivalFieldsUnavailableNote: boolean;
  cancelled: boolean;
  statusUnavailable: boolean;
  stale: boolean;
  lastUpdated: string | null;
  sourceAttribution: string;
}

export interface SendOtpResponse {
  status: string;
}

export interface VerifyOtpResponse {
  customToken: string;
}

export interface ConfirmWatchRequest {
  phoneNumber: string;
  direction: BackendFlightDirection;
  operatingDesignator: string;
  flightDate: string;
}

export interface WatchResponse {
  id: string;
  direction: BackendFlightDirection;
  operatingDesignator: string;
  flightDate: string;
  createdNew: boolean;
  triggersDescription: string[];
}

export interface RecordPlateResponse {
  designator: string;
  direction: BackendFlightDirection;
  windowDays: number;
  sampleSize: number;
  tooThin: boolean;
  medianDelayMinutes: number | null;
  bands: {
    early: { count: number; pct: number };
    onTime: { count: number; pct: number };
    late: { count: number; pct: number };
  };
  onTimeOrEarlyPct: number;
  worstDelay: { minutes: number; date: string } | null;
  cancelledCount: number;
  sourceAttribution: string;
}


export class FlightAlertsService extends ApiService {
  constructor() {
    super(process.env.REACT_APP_api_endpoint!);
  }

  getRecaptchaSiteKey() {
    return this.get<RecaptchaSiteKeyResponse>("/v2/auth/recaptcha-site-key");
  }


  lookupFlight(
    direction: BackendFlightDirection,
    flightNumber: string,
    date: string,
    recaptchaToken: string,
  ) {
    return this.get<FlightCardResponse[]>("/v2/flight-alerts/lookup", {
      params: { direction, flightNumber, date, recaptchaToken },
    });
  }

  getRecordPlate(flightNumber: string, direction: BackendFlightDirection) {
    return this.get<RecordPlateResponse>("/v2/flight-alerts/record-plate", {
      params: { flightNumber, direction },
    });
  }

  sendOtp(phoneNumber: string, recaptchaToken: string) {
    return this.post<SendOtpResponse>("/v2/flight-alerts/otp/send", {
      phoneNumber,
      recaptchaToken,
      platform: "web",
      recaptchaMode: "score",
    });
  }

  verifyOtp(phoneNumber: string, code: string) {
    return this.post<VerifyOtpResponse>("/v2/flight-alerts/otp/verify", { phoneNumber, code });
  }

  confirmWatch(request: ConfirmWatchRequest) {
    return this.post<WatchResponse>("/v2/flight-alerts/watch/confirm", request);
  }
}
